import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Button, FormControl, MenuItem, Select, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import {
    useGetQuoteItemsQuery,
    useLazyGetQuoteItemDrawingQuery,
    useUpdateQuoteItemDrawingMutation,
    useUpdateQuoteItemMutation,
} from '@/app/services/quoteItems'
import { quotesApi, useGetQuoteQuery } from '@/app/services/quotes'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import {
    selectIsCalculatingQuote,
    setIsUpdatingDrawingUnits,
    setOpenDrawingDoctorModal,
    setSelectedQuoteItemDrawingId,
    setSelectedQuoteStatus,
} from '@/app/slices/quoteItemsSlice'
import RenderPartSize from '@/common/components/RenderPartSize/RenderPartSize'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import ThumbnailImage from '@/common/components/ThumbnailImage/ThumbnailImage'
import useNumberFormatter from '@/common/hooks/useNumberFormatter'
import { QUOTE_STATUS_ARRAY } from '@/common/utils'

const classes = {
    ItemDetails_Image: {
        width: '220px',
        gap: '8px',
    },

    ItemDetails_Options_Size: {
        gap: '12px',
    },
    ItemDetails_TextButton: {
        textTransform: 'none',
        fontWeight: 'normal',
        color: 'text.primary',
        fontSize: '14px',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
    },
}

const ItemDrawing = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector((state) => selectOrganisationId(state))
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)

    const { data: selectedQuote, isFetching: isFetchingQuote } = useGetQuoteQuery({
        organisationId,
        quoteId,
    })

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [getQuoteItemDrawing] = useLazyGetQuoteItemDrawingQuery()
    const [updateQuoteItem, { isLoading: isUpdating }] = useUpdateQuoteItemMutation({
        fixedCacheKey: 'shared-quote-item-update',
    })

    const [updateQuoteItemDrawing, { isLoading: isUpdatingDrawing }] = useUpdateQuoteItemDrawingMutation()

    const [itemUnit, setItemUnit] = useState(quoteItem?.isImperial ? 'in' : 'mm')

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const thumbnailUri = useMemo(() => quoteItem?.thumbnailUri, [quoteItem?.thumbnailUri])

    const handleUpdateQuoteItemDrawing = async (quoteItemDrawing) => {
        quoteItemDrawing.paths = Object.values(quoteItemDrawing.paths)
        const drawing = { ...quoteItemDrawing }

        const result = await updateQuoteItemDrawing({
            organisationId,
            quoteId,
            quoteItemId,
            drawing,
        }).unwrap()
        return result
    }

    const handleUpdateQuoteItem = async (quoteItem) => {
        await updateQuoteItem({
            organisationId,
            quoteId: quoteItem?.quoteId,
            quoteItemId: quoteItem?.id,
            quoteItem,
        }).unwrap()
    }

    const handleThumbnailClick = useCallback(() => {
        dispatch(setSelectedQuoteItemDrawingId(quoteItemId))
        dispatch(setOpenDrawingDoctorModal(true))
    }, [dispatch, quoteItemId])

    const handleUnitChange = async (event) => {
        if (selectedQuote.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
            // The awaited item update below already persists status=NotCalculated on the server
            // (UpdateQuoteItemFromDtoV2). Reflect it in the quote cache instead of firing a separate
            // updateQuote PUT, which ran concurrently with the item update and raced it.
            dispatch(
                quotesApi.util.updateQueryData('getQuote', { organisationId, quoteId }, (draft) => {
                    if (draft) {
                        draft.status = QUOTE_STATUS_ARRAY.NotCalculated
                    }
                })
            )
        }
        const value = event?.target?.value
        if (!value || itemUnit === value) return

        const previousUnit = itemUnit
        setItemUnit(value)
        dispatch(setIsUpdatingDrawingUnits(true))

        try {
            const drawing = await getQuoteItemDrawing({ organisationId, quoteId, quoteItemId }, true).unwrap()

            // QM-3403: persist the quote item (which owns isImperial, the field
            // getQuoteItems exposes) BEFORE updating the drawing. updateQuoteItemDrawing
            // invalidates the QuoteItems tag and triggers a getQuoteItems refetch; if the
            // item isn't saved first, that refetch reads stale isImperial and reverts the
            // unit change. This mirrors the working order in DrawingDoctor's PartDetails.
            await handleUpdateQuoteItem({
                ...quoteItem,
                isImperial: value === 'in',
            })

            await handleUpdateQuoteItemDrawing({
                ...drawing,
                isImperial: value === 'in',
            })
        } catch {
            setItemUnit(previousUnit)
            enqueueSnackbar(t('Failed to update the $t(quote) item drawing'), {
                variant: 'error',
            })
        } finally {
            dispatch(setIsUpdatingDrawingUnits(false))
        }
    }

    return (
        <Box
            sx={[
                {
                    alignItems: 'center',
                    display: 'flex',
                    flex: '0 0 auto',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                },
                classes.ItemDetails_Image,
            ]}
        >
            {quoteItem?.partId ? (
                <Typography>
                    {t('Part ID')}: {quoteItem?.partId}
                </Typography>
            ) : null}
            <ThumbnailImage
                uri={thumbnailUri}
                onClick={handleThumbnailClick}
            />
            <Button
                data-testid="nqp-quote-item-open-drawing-doctor"
                sx={classes.ItemDetails_TextButton}
                variant="text"
                onClick={handleThumbnailClick}
            >
                {t('Open Drawing Doctor')}
            </Button>
            {quoteItem && 'profile' in quoteItem ? (
                <Typography variant="strong1">
                    {t('Profile')}: {quoteItem?.sheet?.profile || t(quoteItem?.profile)}
                </Typography>
            ) : null}
            <Box
                sx={[
                    {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.ItemDetails_Options_Size,
                ]}
            >
                <RenderPartSize
                    part={quoteItem}
                    variant="strong2"
                />
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <FormControl
                        color="primary"
                        disabled={isUpdating || isUpdatingDrawing}
                        margin="none"
                        size="small"
                        sx={classes.formControl_small}
                        variant="outlined"
                    >
                        <Select
                            data-testid="nqp-quote-item-select-unit"
                            disabled={
                                isUpdating ||
                                isUpdatingDrawing ||
                                isCalculatingQuote ||
                                isFetchingQuote ||
                                isPartFromLibrary
                            }
                            id="select-itemUnit"
                            labelId="select-itemUnit-label"
                            value={itemUnit}
                            onChange={handleUnitChange}
                        >
                            <MenuItem value="mm">{t('Millimetres')}</MenuItem>
                            <MenuItem value="in">{t('Inches')}</MenuItem>
                        </Select>
                    </FormControl>
                </TbxTooltip>
            </Box>
        </Box>
    )
}
ItemDrawing.propTypes = {
    quoteItemId: PropTypes.string,
}
export default memo(ItemDrawing)
